/**
 * GlobalNavBar — the single nav bar used across every v2 surface.
 *
 * Owns the GLOBAL chrome that must stay identical on every page:
 *   - the user profile (sourced once from `useAuth`, so the name/avatar never
 *     gets lost or hardcoded as you move between pages)
 *   - the project list + switcher (`useProjects`)
 *   - the nav handlers (switch project, create, settings, log out)
 *
 * Pages pass only their own context — the current project, the active tab,
 * and whether to show the tab strip — via props. Everything else is derived,
 * so a page just renders `<GlobalNavBar … />` and gets a consistent bar.
 */

import { useCallback, useMemo } from 'react';
import { useLocation } from 'wouter';

import { useAuth } from '@/hooks/useAuth';
import { useProjects } from '@/hooks/v2/useProjects';
import { useUserOrgs } from '@/hooks/v2/useUserOrgs';
import { NavBar } from '@/components/v2/layout/NavBar';
import {
  NO_CURRENT_PROJECT_ID,
  type Project,
} from '@/components/v2/layout/ProjectTopBar';
import { type UserInfo } from '@/components/v2/layout/UserProfileMenu';
import type { ProjectTabId } from '@/pages/v2/projectTabs';
import { isActiveProjectStatus } from '@shared/realtime-types';

function getInitials(name: string): string {
  const segments = name.trim().split(/\s+/).filter(Boolean);
  if (segments.length === 0) return 'U';
  return (
    segments
      .slice(0, 2)
      .map((segment) => segment.charAt(0))
      .join('')
      .toUpperCase() || 'U'
  );
}

export interface GlobalNavBarProps {
  /** Page context. When omitted, the picker shows a "Select a project" label. */
  currentProject?: Project;
  /** Active project tab (only meaningful when `showTabs`). */
  activeTab?: ProjectTabId;
  /** Render the tab strip below the top bar. Off by default (home, 404, …). */
  showTabs?: boolean;
  variant?: 'home' | 'project';
  /** Lock the project switcher to a static label (no switching) — e.g. scoping. */
  lockProject?: boolean;
  /**
   * Hide the project switcher + static label entirely (no centre content) —
   * e.g. the Admin Hub, which isn't project-scoped (the `isAdmin` force-show
   * would otherwise surface the picker).
   */
  hideProjectSwitcher?: boolean;
  className?: string;
}

export function GlobalNavBar({
  currentProject,
  activeTab = 'overview',
  showTabs = false,
  variant = 'project',
  lockProject = false,
  hideProjectSwitcher = false,
  className,
}: GlobalNavBarProps) {
  const auth = useAuth();
  const projectsQuery = useProjects();
  const { orgs, currentOrgId, switchOrg } = useUserOrgs();
  const [, setLocation] = useLocation();

  const user: UserInfo = useMemo(() => {
    const u = auth.user;
    const name = u?.full_name || u?.email || 'Anaka user';
    const initialsSource =
      u?.full_name && u.full_name.trim().length > 0
        ? u.full_name
        : u?.email
          ? u.email.charAt(0)
          : 'U';
    return {
      name,
      company: '',
      avatarUrl: u?.avatar_url,
      initials: getInitials(initialsSource),
    };
  }, [auth.user]);

  const projects: Project[] = useMemo(
    () =>
      (projectsQuery.data ?? []).map((p) => ({
        id: p.id,
        name: p.name,
        short_id: p.short_id,
        updated_at: p.updated_at,
        // Gates the switcher dropdown — see ProjectTopBar (ANA-266).
        active: isActiveProjectStatus(p.status),
      })),
    [projectsQuery.data],
  );

  const resolvedCurrentProject: Project = currentProject ?? {
    id: NO_CURRENT_PROJECT_ID,
    name: projects.length > 0 ? 'Select a project' : 'No project selected',
  };

  // Status of the project the page is showing — drives status-aware tab
  // visibility (early-lifecycle projects hide the not-yet-populated tabs).
  // Sourced from the same `useProjects` list that feeds the switcher, matched
  // by id; undefined until the list resolves → the full strip shows.
  const currentProjectStatus = useMemo(
    () =>
      currentProject
        ? (projectsQuery.data ?? []).find((p) => p.id === currentProject.id)?.status
        : undefined,
    [projectsQuery.data, currentProject],
  );

  const handleProjectSelect = useCallback(
    (id: string) => {
      if (id === NO_CURRENT_PROJECT_ID) return;
      const target = (projectsQuery.data ?? []).find((p) => p.id === id);
      if (target) setLocation(`/projects/${target.short_id}/overview`);
    },
    [projectsQuery.data, setLocation],
  );
  const handleCreateProject = useCallback(
    () => setLocation('/projects/new'),
    [setLocation],
  );
  const handleLogOut = useCallback(() => {
    void auth.logout();
  }, [auth]);

  return (
    <NavBar
      currentProject={resolvedCurrentProject}
      projects={projects}
      user={user}
      activeTab={activeTab}
      showTabs={showTabs}
      variant={variant}
      lockProject={lockProject}
      hideProjectSwitcher={hideProjectSwitcher}
      projectStatus={currentProjectStatus}
      onProjectSelect={handleProjectSelect}
      onCreateProject={handleCreateProject}
      onLogOut={handleLogOut}
      orgs={orgs}
      currentOrgId={currentOrgId}
      onOrgSwitch={switchOrg}
      isAdmin={auth.user?.role === 'admin'}
      className={className}
    />
  );
}
